/**
 * Ostendo resource catalog — the single source of truth for every resource the
 * `query_ostendo*` tools expose, with the metadata the access-mapping UI needs
 * (GH #922, the explorer feeding the #876 granular permission matrix).
 *
 * The enforcement-side module map (`OSTENDO_RESOURCE_MODULE` in rbac.ts) is
 * DERIVED from this catalog, so the two can't drift. A parity test
 * (tests/ostendo-catalog.test.ts) additionally pins this catalog against the
 * handler's own "Available:" resource list in query-ostendo-handlers.ts —
 * adding a resource to the handler without cataloguing it fails CI.
 *
 * Two database fields, one meaning each (#2195):
 *  - `exists_on`: every Ostendo database the resource's tables are on.
 *    COOP = Coopers (manufacturing arm, configuration 4), CROS = Crosbie
 *    National (sales arm, configuration 5). Filled from the live schema
 *    probes in .jez/research/ostendo-live-schema.json (unlabelled row-count
 *    entries are the configuration-4 dump per OSTENDO_LIVE_SCHEMA.md; a
 *    `_probe`/`probe` note names the database when it was not COOP). Where
 *    the probe file is silent on a database the entry keeps the database an
 *    earlier live read established (cited per entry) — silence is not
 *    disproof. The handler refuses a single-database resource off that
 *    database, so narrowing a list here is a refusal in production.
 *  - `default_db`: where the legacy `query_ostendo` tool routes the resource
 *    when the caller names no db. The handler derives its routing set from
 *    this field. Always a member of `exists_on`.
 * Every entry is live: tests/ostendo-catalog.test.ts fails on an empty
 * `exists_on`, so a retired resource is removed, never left as a stub.
 */

export type OstendoDb = "COOP" | "CROS";

export type OstendoModule = "S" | "P" | "I" | "A";

export const OSTENDO_MODULE_LABELS: Record<OstendoModule, string> = {
  S: "Sales",
  P: "Purchasing",
  I: "Inventory",
  A: "Assembly",
};

export interface OstendoResourceInfo {
  /** The `resource` arg value the query_ostendo* tools accept. */
  resource: string;
  /** S/P/I/A module classification (Hugo's GH #94 matrix). */
  module: OstendoModule;
  /** Every Ostendo database the resource exists on (see the header note). */
  exists_on: OstendoDb[];
  /** Where the legacy tool routes it when no db is named. Member of exists_on. */
  default_db: OstendoDb;
  /** Short human label for the explorer UI. */
  label: string;
  /** One-line description (sourced from the tool definitions — don't invent). */
  description: string;
}

export const OSTENDO_RESOURCE_CATALOG: OstendoResourceInfo[] = [
  // ── Sales (S) — customer, sales orders/invoices, pricing exposure ──
  {
    resource: "customers",
    module: "S",
    // CustomerMaster: COOP proven (probe, 19 rows); CROS = the 650 real customers (#94 routing default), unprobed in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "CROS",
    label: "Customers",
    description: "Customer master: 650 real customers on CROS; COOP entries are internal cost-codes exposed only through the bounded integration projection.",
  },
  {
    resource: "sales_orders",
    module: "S",
    // SalesHeader + SalesLines: COOP proven (probe); CROS = the 27,599 customer orders (#94), unprobed in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "CROS",
    label: "Sales Orders",
    description: "Customer order headers (27,599 orders, history from 2006). Header-level — no product column.",
  },
  {
    resource: "sales_invoices",
    module: "S",
    // SalesInvoiceHeader: COOP proven (probe); CROS = the 29,022 invoices (#94), unprobed in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "CROS",
    label: "Sales Invoices",
    description: "Billing / invoice records.",
  },
  {
    resource: "deliveries",
    module: "S",
    // SalesDeliveryHeader: CROS read live by the #1003 freight panel (SHIPPINGMETHOD /
    // CALCULATEDWEIGHT / NOOFSHIPPINGPIECES / DELIVERYSTATE) and the #1860 recall
    // trace (DELIVERYNO / ORDERNUMBER / DELIVERYSTATUS). COOP despatch is not a
    // customer delivery, so CROS only (#2226).
    exists_on: ["CROS"],
    default_db: "CROS",
    label: "Deliveries",
    description: "Despatch headers on CROS — SalesDeliveryHeader, one row per delivery with carrier (SHIPPINGMETHOD), CALCULATEDWEIGHT in kg and NOOFSHIPPINGPIECES by DELIVERYDATE; the tonnage-by-carrier path (#2226).",
  },
  {
    resource: "transfers",
    module: "S",
    // #2461 (epic #2235 shape C): SalesDeliveryHeader UNION ALL
    // InventoryTransfers with CUSTOMERSHIPPINGMETHODS joined. The delivery arm
    // is the CROS table the #2226 deliveries resource already reads live; the
    // transfer arm and the shipping-method master were read live on CROS by a
    // FIRST 1 probe on 13 Sep 2026 (CUSTOMERSHIPPINGMETHODS is a methods
    // master keyed by SHIPPINGMETHOD, no customer column). CROS only, like
    // deliveries: a COOP union would answer a delivery question off the
    // manufacturing arm.
    exists_on: ["CROS"],
    default_db: "CROS",
    label: "Transfers & Deliveries",
    description: "Transport shape on CROS - inventory transfers and customer deliveries in one set with carrier (SHIPPINGMETHOD), GROUPTYPE, tonnes (CALCULATEDWEIGHT kg/1000) and freight cost by TRANSFERDATE; the by-carrier tonnes-and-cost path (#2461). ZONE, UNLOADTYPE and CRANECOST are not held and are named as unavailable on every result.",
  },
  {
    resource: "sales_history",
    module: "S",
    // SalesHistory: CROS proven (probe, FIRST-0 cfg5); COOP = intercompany sales, read live in #1739, unprobed in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "CROS",
    label: "Sales History",
    description: "Line-level shipped-order archive (~79,000 lines) — the path for product-level sales questions.",
  },
  {
    resource: "budget",
    module: "S",
    // BUDGETDETAIL: probe names no database. Kept at CROS per #189 (the budget series live there); unproven.
    exists_on: ["CROS"],
    default_db: "CROS",
    label: "Budgets & Forecasts",
    description: "Sales/production budgets per product per month — Qty/Revenue/Cost/Contribution, FY2021–2027. Carries revenue + margin.",
  },
  // ── Purchasing (P) — suppliers, receipts, POs, invoices, cost data (this order is the tools' description order, #2208) ──
  {
    resource: "suppliers",
    module: "P",
    // SupplierMaster: COOP proven (probe); CROS = the carrier suppliers read live in #2173, unprobed in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "COOP",
    label: "Suppliers",
    description: "Supplier master (both DBs).",
  },
  {
    resource: "receipts",
    module: "P",
    // PurchaseReceipts + PurchaseReceiptLines: COOP proven (probe); CROS = freight receipts read live in #2173, unprobed in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "COOP",
    label: "Goods Received",
    description: "Purchase receipts — goods received against POs (CROS carries purchased services such as freight, #2173).",
  },
  {
    resource: "purchase_orders",
    module: "P",
    // PurchaseHeader + PurchaseLines: COOP proven (probe); CROS = P&J Harvey POs read live in #2173, unprobed in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "COOP",
    label: "Purchase Orders",
    description: "Purchase orders raised on suppliers.",
  },
  {
    resource: "purchase_invoices",
    module: "P",
    // PURCHASEINVOICES: CROS proven (probe, #2177). Not probed on COOP (PurchaseInvoiceLines is, so the header may exist there too); CROS-only until read.
    exists_on: ["CROS"],
    default_db: "CROS",
    label: "Supplier Invoices",
    description: "Supplier (AP) invoices on CROS — PURCHASEINVOICES, header-level nett + total by supplier and PURCHASEINVDATE. The system of record for freight carriers invoiced without a purchase order (#2177).",
  },
  {
    resource: "cost_history",
    module: "P",
    // ItemCostHistory: probe names no database. COOP = the ~18K-row materialise path; CROS = the ~169K-row Tier B path (COUNT read live 2026-07-23, #1046). Neither in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "COOP",
    label: "Cost History",
    description: "Material price changes over time. Cost/pricing exposure.",
  },
  // ── Inventory (I) — items, materials, batches, movements ──
  {
    resource: "items",
    module: "I",
    // ItemMaster: COOP proven (probe); CROS = the sales catalogue (#94), unprobed in the file.
    exists_on: ["COOP", "CROS"],
    default_db: "COOP",
    label: "Items",
    description: "Product catalogue (COOP manufacturing catalogue; CROS sales catalogue).",
  },
  {
    resource: "materials",
    module: "I",
    // DescriptorMaster: COOP proven (probe).
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Raw Materials",
    description: "Raw materials master including prices.",
  },
  {
    resource: "batches",
    module: "I",
    // ItemBatches + ItemMaster: COOP proven (probe). CROS carries the copy that
    // reverse trace and recall read, so the manifest can qualify either database.
    exists_on: ["COOP", "CROS"],
    default_db: "COOP",
    label: "Batches",
    description: "Lot/QC batch records. Reverse trace and recall also read the CROS copy.",
  },
  {
    resource: "inventory",
    module: "I",
    // ItemBatches + ItemMaster: COOP proven (probe).
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Inventory",
    description: "Stock levels.",
  },
  {
    resource: "stock_adjustments",
    module: "I",
    // InventoryAdjustments: COOP proven (probe); InventoryAdjustLines probe names no database.
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Stock Adjustments",
    description: "Manual stock adjustment records.",
  },
  {
    resource: "batch_status_log",
    module: "I",
    // ItemBatchStatusLog: COOP proven (probe).
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Batch Status Log",
    description: "QC audit trail of batch status changes.",
  },
  {
    resource: "inventory_transactions",
    module: "I",
    // InventoryTransactions: probe names no database. Kept at both per #874/#94 (SALESISSUE rows read live on CROS); unproven.
    exists_on: ["COOP", "CROS"],
    default_db: "COOP",
    label: "Inventory Transactions",
    description: "Raw movement ledger — interleaves SALESISSUE (CROS customer shipments) and PURCHASERECEIPT rows, so it crosses module boundaries.",
  },
  {
    resource: "inventory_by_location",
    module: "I",
    // InventoryTransactions: probe names no database. Kept at both per #874 (reconciled live on both sides 15 Jul 2026); unproven.
    exists_on: ["COOP", "CROS"],
    default_db: "COOP",
    label: "Inventory by Location",
    description: "Per-warehouse/location on-hand (#874) — CROS covers the distribution sites, COOP the factory side.",
  },
  // ── Assembly (A) — BOMs, production orders, output, labour ──
  {
    resource: "boms",
    module: "A",
    // BomMaster + BomLines: COOP proven (probe).
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "BOMs / Formulations",
    description: "Bill-of-materials master + lines (product recipes).",
  },
  {
    resource: "bom_changes",
    module: "A",
    // BOMChangeHistory: probe names no database; ItemMaster join COOP proven. Kept at COOP; unproven.
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "BOM Changes",
    description: "Formulation change history.",
  },
  {
    resource: "production_orders",
    module: "A",
    // AssemblyHeader: COOP proven (probe).
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Production Orders",
    description: "Assembly order headers.",
  },
  {
    resource: "production_output",
    module: "A",
    // AssemblyReceipts + ItemMaster: COOP proven (probe).
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Production Output",
    description: "Output recorded against assembly orders.",
  },
  {
    resource: "production_issues",
    module: "A",
    // AssemblyIssueLines: probe names no database. Kept at COOP; unproven.
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Production Issues",
    description: "Materials issued to assembly orders.",
  },
  {
    resource: "labour",
    module: "A",
    // TimesheetLines: probe names no database. Kept at COOP; unproven.
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Labour",
    description: "Production timesheets — hours per assembly order (#105).",
  },
  {
    resource: "assembly_lines",
    module: "A",
    // AssemblyLines: probe names no database. Kept at COOP; unproven.
    exists_on: ["COOP"],
    default_db: "COOP",
    label: "Assembly Lines",
    description: "BOM plan-vs-actual per assembly order — planned ORDERQTY vs actual TOTALISSUEDQTY (#108).",
  },
];

/** Resource → module lookup derived from the catalog (consumed by rbac.ts). */
export const OSTENDO_RESOURCE_MODULE_FROM_CATALOG: Record<string, OstendoModule> =
  Object.fromEntries(OSTENDO_RESOURCE_CATALOG.map((r) => [r.resource, r.module]));

/** Every catalogued resource name — what the explorer UI offers for granting. */
export const LIVE_OSTENDO_RESOURCES: string[] = OSTENDO_RESOURCE_CATALOG.map((r) => r.resource);

/** Resource → the databases it exists on (consumed by the query_ostendo handler's routing gate). */
export const OSTENDO_RESOURCE_EXISTS_ON: ReadonlyMap<string, readonly OstendoDb[]> = new Map(
  OSTENDO_RESOURCE_CATALOG.map((r) => [r.resource, r.exists_on]),
);

/** Resources the legacy tool routes to CROS when the caller names no db. */
export const CROS_DEFAULT_RESOURCES: ReadonlySet<string> = new Set(
  OSTENDO_RESOURCE_CATALOG.filter((r) => r.default_db === "CROS").map((r) => r.resource),
);

/**
 * #2891: the exact query_ostendo DB-routing decision, extracted verbatim
 * from query-ostendo-handlers.ts's inline ternary (behaviour unchanged, the
 * handler now calls this instead of repeating it) so prompt-suggestions.ts
 * can replay the SAME db a past tool call actually ran against, without a
 * second hand-copy of the routing rule that could drift from the real one.
 * `adjustSource` is read from a stored tool call's `input.adjust_source`
 * (or, at the handler, `args.adjust_source`) and only matters for the
 * inventory_transactions SALESISSUE auto-route.
 */
export function resolveOstendoDb(
  toolName: string,
  resource: string,
  requestedDb: string | undefined,
  adjustSource: unknown,
): "COOP" | "CROS" {
  if (toolName === "query_ostendo_coop") return "COOP";
  if (toolName === "query_ostendo_cros") return "CROS";
  if (requestedDb === "CROS") return "CROS";
  if (requestedDb === "COOP") return "COOP";
  if (CROS_DEFAULT_RESOURCES.has(resource)) return "CROS";
  if (resource === "inventory_transactions" && String(adjustSource || "").toUpperCase() === "SALESISSUE") return "CROS";
  return "COOP";
}
